// Pure, DOM-free display helpers for the three tier report components
// (TierDistributionReport / TierRosterReport / TierUpLogReport). Kept out of
// the .vue files so tests/unit/tierReportDisplay.spec.ts can exercise them
// without a component-mount harness — this repo's yarn test:unit runs under
// plain Node (see playwright.unit.config.ts). Mirrors tierDisplay.js's
// existing split for exactly the same reason.

// --- Distribution: pivot the flat {tierId, tierName, branchId, branchName,
// patientCount} rows the backend returns (see tier_repo/getTierDistribution.js)
// into a branch-by-tier matrix, which is what "headcount per tier per
// branch" actually means as a report. ---

/**
 * @param {Array<{tierId:number, tierName:string, branchId:number,
 *   branchName:string, patientCount:number}>} rows
 * @returns {{
 *   tierColumns: Array<{key:string, label:string}>,
 *   branchRows: Array<Object>,
 *   totalsRow: Object,
 * }}
 */
export function buildDistributionPivot(rows) {
  const list = Array.isArray(rows) ? rows : [];

  // Distinct tiers, first-seen order. The backend orders rows by
  // `t.rank ASC, b.name ASC` (see getTierDistribution.js), and a tier's rows
  // are contiguous within that ordering, so the first occurrence of each
  // distinct tierId already walks tiers in rank order — no separate rank
  // field needs to travel through this pivot.
  const tierOrder = [];
  const seenTiers = new Set();

  // branchId -> { branchId, branchName, counts: Map(tierId -> count) }
  const branchMap = new Map();

  for (const r of list) {
    if (!r) continue;
    if (!seenTiers.has(r.tierId)) {
      seenTiers.add(r.tierId);
      tierOrder.push({ id: r.tierId, name: r.tierName });
    }
    if (!branchMap.has(r.branchId)) {
      branchMap.set(r.branchId, { branchId: r.branchId, branchName: r.branchName, counts: new Map() });
    }
    const b = branchMap.get(r.branchId);
    b.counts.set(r.tierId, (b.counts.get(r.tierId) || 0) + Number(r.patientCount || 0));
  }

  const tierColumns = tierOrder.map(t => ({ key: `tier_${t.id}`, label: t.name }));

  const branchRows = [...branchMap.values()]
    .sort((a, b) => String(a.branchName).localeCompare(String(b.branchName), "th"))
    .map(b => {
      const row = { branchId: b.branchId, branchName: b.branchName };
      let total = 0;
      for (const t of tierOrder) {
        const count = b.counts.get(t.id) || 0;
        row[`tier_${t.id}`] = count;
        total += count;
      }
      row._total = total;
      return row;
    });

  const totalsRow = { branchName: "รวมทุกสาขา", _isTotal: true };
  let grandTotal = 0;
  for (const t of tierOrder) {
    const sum = branchRows.reduce((acc, r) => acc + r[`tier_${t.id}`], 0);
    totalsRow[`tier_${t.id}`] = sum;
    grandTotal += sum;
  }
  totalsRow._total = grandTotal;

  return { tierColumns, branchRows, totalsRow };
}

// --- Roster: patient_tier_state.status is ENUM("ACTIVE","GRACE") only (see
// models/patient_tier_state.js) — no third value exists, but a fallback to
// the raw code keeps this from silently rendering blank if that ever
// changes. ---

const ROSTER_STATUS_LABELS = { ACTIVE: "ใช้งาน", GRACE: "ช่วงต่อสิทธิ์" };

export function rosterStatusLabel(status) {
  return ROSTER_STATUS_LABELS[status] || status || "—";
}

// --- Tier-up log: the mismatch flag. ---
//
// This is the entire justification for tier vouchers being bearer
// (no patient binding enforced) instead of pinned to the issued patient —
// see the header comment on tier_repo/getTierUpLog.js. A row with
// mismatch=true means the code was redeemed by someone other than the
// patient it was issued to: transferred or sold. That must never render as
// something a viewer can miss while skimming the table, so this returns a
// full sentence naming BOTH sides (who it was issued to, who actually used
// it) rather than a bare boolean or a tooltip-only icon.
//
// redeemedByPatientId is the only thing the backend gives us about the
// redeemer (see getTierUpLog.js's return shape) — no name, no HN. Showing
// "รหัสคนไข้ #<id>" is honest about what's actually known rather than
// inventing or omitting a name.

/**
 * @param {{mismatch:boolean, patientName?:string, patientId?:number,
 *   redeemedByPatientId:number|null}} row
 * @returns {string|null} null when there is nothing to warn about.
 */
export function tierUpMismatchWarning(row) {
  if (!row || !row.mismatch) return null;
  const owner = row.patientName || (row.patientId != null ? `รหัสคนไข้ #${row.patientId}` : "ไม่ทราบคนไข้");
  const redeemer = row.redeemedByPatientId != null
    ? `รหัสคนไข้ #${row.redeemedByPatientId}`
    : "ไม่ทราบคนไข้"; // should not happen — mismatch is only ever true when redeemedByPatientId is set (see getTierUpLog.js's CASE WHEN ... IS NOT NULL guard).
  return `รหัสบัตรถูกใช้ต่างคนไข้! ออกให้ ${owner} แต่ถูกใช้จริงโดย ${redeemer} — อาจมีการส่งต่อหรือขายรหัส`;
}

export function tierUpRowVariant(row) {
  return row && row.mismatch ? "danger" : null;
}
